import { useState, type FormEvent } from 'react'
import { KeyRound } from 'lucide-react'
import { Button, ErrorNote, Field, Input } from '@/components/ui'
import { CODE_LENGTH, CODE_PREFIX, normalizeCode } from '@shared/codes'
import { activateCode } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'
import { useToast } from '@/components/Toast'

/** Visar det användaren skrivit som LB-XXXX-XXXX medan hen skriver. */
function displayValue(raw: string): string {
  let cleaned = raw.toUpperCase().replace(/[^0-9A-Z]/g, '')
  if (cleaned.startsWith(CODE_PREFIX)) cleaned = cleaned.slice(CODE_PREFIX.length)
  cleaned = cleaned.slice(0, CODE_LENGTH)

  if (!cleaned) return ''
  if (cleaned.length <= 4) return `${CODE_PREFIX}-${cleaned}`
  return `${CODE_PREFIX}-${cleaned.slice(0, 4)}-${cleaned.slice(4)}`
}

export function ActivateCodeForm({
  initialCode = '',
  onActivated,
}: {
  initialCode?: string
  onActivated?: (result: { periodName: string; startsInFuture: boolean; startDate: string }) => void
}) {
  const toast = useToast()
  const [value, setValue] = useState(() => displayValue(initialCode))
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const normalized = normalizeCode(value)
  const complete = normalized !== null

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (!complete) {
      setError(`Koden består av ${CODE_LENGTH} tecken efter ${CODE_PREFIX}-. Kontrollera lappen.`)
      return
    }
    /*
     * Feltryck avgörs av servern, inte här.
     *
     * Kontrolltecknet hade kunnat prövas i webbläsaren och spara ett anrop.
     * Men då ser ingen hur många som skriver fel – och med tryckta lappar är
     * det varningen för att trycket är svårläst. Meddelandet till köparen är
     * detsamma; det tar bara några hundradelar längre.
     */
    const cameFromQr = initialCode !== '' && normalizeCode(initialCode) === normalized

    setBusy(true)
    try {
      const result = await activateCode({ code: normalized, via: cameFromQr ? 'qr' : 'typed' })
      toast.success(
        result.startsInFuture
          ? `Koden är registrerad. Häftet öppnas ${result.startDate}.`
          : `Häftet ${result.periodName} är aktiverat. Varsågod!`,
      )
      setValue('')
      onActivated?.(result)
    } catch (caught) {
      setError(authErrorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {error && <ErrorNote>{error}</ErrorNote>}

      <Field
        label="Kod från lappen"
        hint="Står tryckt på lappen du köpte, till exempel LB-A3K9-M2XP."
        required
      >
        {({ id, describedBy }) => (
          <Input
            id={id}
            aria-describedby={describedBy}
            value={value}
            onChange={(event) => setValue(displayValue(event.target.value))}
            placeholder="LB-XXXX-XXXX"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            className="text-center font-mono text-xl tracking-[0.2em]"
          />
        )}
      </Field>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={busy}
        disabled={!complete}
        icon={<KeyRound size={18} />}
      >
        Aktivera häftet
      </Button>
    </form>
  )
}
